import {UserDefinedObjectTemplateAdditionalColumnType as UdoColumnType} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplateAdditionalColumn";
import type {CapabilityUdoColumnResolver} from "~/utilities/capabilityUdoTemplateColumns";

import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";
import {OttopilotEventViewPanelSectionKind, type OttopilotEventTableViewColumn} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTableViewColumn";
import type {EventContainerTemplate as JourneysModuleConfiguration} from "~/domainModels/workspaceModules/eventContainersModule";
import {AnankeClockIcon, AnankeLinkExternalIcon, AnankePersonIcon, AnankeTagIcon} from "~/domainModels/workspaceSpecific/icons";
import {
    AmaronEventTypes,
    AMARON__EVENT__SERVICE__BOOKED_ON__COLUMN_ID,
    AMARON__EVENT__CALLS__ACTION_PLATFORM__COLUMN_ID,
    AMARON__EVENT__CALLS__CALL_COUNT__COLUMN_ID,
    AMARON__EVENT__CALLS__CALL_STATUS__COLUMN_ID,
    AMARON__EVENT__CALLS__CANCELLED_BY__COLUMN_ID,
    AMARON__EVENT__CALLS__CANCELLED_ON__COLUMN_ID,
    AMARON__EVENT__CALLS__CANCELLATION_REASON__COLUMN_ID,
    AMARON__EVENT__CALLS__CUSTOMER_CITY__COLUMN_ID,
    AMARON__EVENT__CALLS__CUSTOMER_STATE__COLUMN_ID,
    AMARON__EVENT__CALLS__FAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT__CALLS__FAD_SOURCE__COLUMN_ID,
    AMARON__EVENT__CALLS__FBCLID__COLUMN_ID,
    AMARON__EVENT__CALLS__FBRAID__COLUMN_ID,
    AMARON__EVENT__CALLS__GAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT__CALLS__GAD_SOURCE__COLUMN_ID,
    AMARON__EVENT__CALLS__GBRAID__COLUMN_ID,
    AMARON__EVENT__CALLS__GCLID__COLUMN_ID,
    AMARON__EVENT__CALLS__L1_DISPOSITION__COLUMN_ID,
    AMARON__EVENT__CALLS__L2_DISPOSITION__COLUMN_ID,
    AMARON__EVENT__CALLS__L3_DISPOSITION__COLUMN_ID,
    AMARON__EVENT__CALLS__L4_DISPOSITION__COLUMN_ID,
    AMARON__EVENT__CALLS__LAST_CALL_DATE__COLUMN_ID,
    AMARON__EVENT__CALLS__LOGGED_BY__COLUMN_ID,
    AMARON__EVENT__CALLS__LOGGED_ON__COLUMN_ID,
    AMARON__EVENT__CALLS__REMARKS__COLUMN_ID,
    AMARON__EVENT__CALLS__SCHEDULED_BY__COLUMN_ID,
    AMARON__EVENT__CALLS__SCHEDULED_ON__COLUMN_ID,
    AMARON__EVENT__CALLS__UTM_CAMPAIGN__COLUMN_ID,
    AMARON__EVENT__CALLS__UTM_CONTENT__COLUMN_ID,
    AMARON__EVENT__CALLS__UTM_MEDIUM__COLUMN_ID,
    AMARON__EVENT__CALLS__UTM_PLATFORM__COLUMN_ID,
    AMARON__EVENT__CALLS__UTM_SOURCE__COLUMN_ID,
    AMARON__EVENT__CALLS__UTM_TERM__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__FAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__FAD_SOURCE__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__FBCLID__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__FBRAID__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__GAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__GAD_SOURCE__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__GBRAID__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__GCLID__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__SMARTPING_CAMPAIGN__COLUMN_ID,
    AMARON__EVENT__SERVICE__FAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT__SERVICE__FAD_SOURCE__COLUMN_ID,
    AMARON__EVENT__SERVICE__FBCLID__COLUMN_ID,
    AMARON__EVENT__SERVICE__FBRAID__COLUMN_ID,
    AMARON__EVENT__SERVICE__GAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT__SERVICE__GAD_SOURCE__COLUMN_ID,
    AMARON__EVENT__SERVICE__GBRAID__COLUMN_ID,
    AMARON__EVENT__SERVICE__GCLID__COLUMN_ID,
    AMARON__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID,
} from "~/domainModels/workspaceSpecific/amaron";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {uniqueUuid} from "@vesta/unique/unique";

function normalizeLabel(value: string): string {
    return value.trim().toLowerCase();
}

const AMARON_EVENT_ATTRIBUTION_VIEW_COLUMNS: Readonly<Record<string, ReadonlyArray<{columnId: Uuid; label: string}>>> = {
    [AmaronEventTypes.Calls]: [
        {columnId: AMARON__EVENT__CALLS__GAD_SOURCE__COLUMN_ID, label: "Google Ads Source"},
        {columnId: AMARON__EVENT__CALLS__GAD_CAMPAIGN_ID__COLUMN_ID, label: "Google Ads Campaign ID"},
        {columnId: AMARON__EVENT__CALLS__GBRAID__COLUMN_ID, label: "GBRAID"},
        {columnId: AMARON__EVENT__CALLS__GCLID__COLUMN_ID, label: "GCLID"},
        {columnId: AMARON__EVENT__CALLS__FAD_SOURCE__COLUMN_ID, label: "Facebook Ads Source"},
        {columnId: AMARON__EVENT__CALLS__FAD_CAMPAIGN_ID__COLUMN_ID, label: "Facebook Ads Campaign ID"},
        {columnId: AMARON__EVENT__CALLS__FBRAID__COLUMN_ID, label: "FBRAID"},
        {columnId: AMARON__EVENT__CALLS__FBCLID__COLUMN_ID, label: "FBCLID"},
    ],
    [AmaronEventTypes.Enquiry]: [
        {columnId: AMARON__EVENT__ENQUIRY__SMARTPING_CAMPAIGN__COLUMN_ID, label: "Smartping Campaign"},
        {columnId: AMARON__EVENT__ENQUIRY__GAD_SOURCE__COLUMN_ID, label: "Google Ads Source"},
        {columnId: AMARON__EVENT__ENQUIRY__GAD_CAMPAIGN_ID__COLUMN_ID, label: "Google Ads Campaign ID"},
        {columnId: AMARON__EVENT__ENQUIRY__GBRAID__COLUMN_ID, label: "GBRAID"},
        {columnId: AMARON__EVENT__ENQUIRY__GCLID__COLUMN_ID, label: "GCLID"},
        {columnId: AMARON__EVENT__ENQUIRY__FAD_SOURCE__COLUMN_ID, label: "Facebook Ads Source"},
        {columnId: AMARON__EVENT__ENQUIRY__FAD_CAMPAIGN_ID__COLUMN_ID, label: "Facebook Ads Campaign ID"},
        {columnId: AMARON__EVENT__ENQUIRY__FBRAID__COLUMN_ID, label: "FBRAID"},
        {columnId: AMARON__EVENT__ENQUIRY__FBCLID__COLUMN_ID, label: "FBCLID"},
    ],
    [AmaronEventTypes.Service]: [
        {columnId: AMARON__EVENT__SERVICE__GAD_SOURCE__COLUMN_ID, label: "Google Ads Source"},
        {columnId: AMARON__EVENT__SERVICE__GAD_CAMPAIGN_ID__COLUMN_ID, label: "Google Ads Campaign ID"},
        {columnId: AMARON__EVENT__SERVICE__GBRAID__COLUMN_ID, label: "GBRAID"},
        {columnId: AMARON__EVENT__SERVICE__GCLID__COLUMN_ID, label: "GCLID"},
        {columnId: AMARON__EVENT__SERVICE__FAD_SOURCE__COLUMN_ID, label: "Facebook Ads Source"},
        {columnId: AMARON__EVENT__SERVICE__FAD_CAMPAIGN_ID__COLUMN_ID, label: "Facebook Ads Campaign ID"},
        {columnId: AMARON__EVENT__SERVICE__FBRAID__COLUMN_ID, label: "FBRAID"},
        {columnId: AMARON__EVENT__SERVICE__FBCLID__COLUMN_ID, label: "FBCLID"},
    ],
};

const AMARON_EVENT_ATTRIBUTION_VIEW_SECTION_IDS: Readonly<Record<string, Uuid>> = {
    [AmaronEventTypes.Calls]: uniqueUuid`amaron-call-view-utm-attribution-fallback`,
    [AmaronEventTypes.Enquiry]: uniqueUuid`amaron-enquiry-view-utm-attribution`,
    [AmaronEventTypes.Service]: uniqueUuid`amaron-service-view-utm-attribution`,
};

/** Ensures every Amaron event view panel exposes the eight attribution fields in its UTM section. */
export function ensureAmaronEventAttributionViewPanel(eventTemplate: OttopilotEventTemplate): OttopilotEventTemplate {
    const attributionColumns = AMARON_EVENT_ATTRIBUTION_VIEW_COLUMNS[eventTemplate.id];
    const viewPanel = eventTemplate.configuration.eventPage?.viewPanel;
    if (attributionColumns === undefined || viewPanel === undefined) {
        return eventTemplate;
    }

    const toPanelColumns = (columns: ReadonlyArray<{columnId: Uuid; label: string}>) =>
        columns.map((column) => ({
            id: `${String(column.columnId)}::${normalizeLabel(column.label)}` as Uuid,
            label: column.label,
            columnId: column.columnId,
            columnType: "OttopilotEvent" as const,
            eventTemplateId: eventTemplate.id,
            isAdditionalColumn: true as const,
        }));
    const hasUtmSection = viewPanel.sections.some(
        (section) =>
            section.kind !== OttopilotEventViewPanelSectionKind.eventOverview &&
            ["utm parameter", "utm parameters"].includes(normalizeLabel(section.displayName)),
    );
    const updatedSections = viewPanel.sections.map((section) => {
        if (
            section.kind === OttopilotEventViewPanelSectionKind.eventOverview ||
            !["utm parameter", "utm parameters"].includes(normalizeLabel(section.displayName))
        ) {
            return section;
        }
        const existingColumnIds = new Set(section.columns.map((column) => String(column.columnId)));
        const missingColumns = attributionColumns.filter((column) => !existingColumnIds.has(String(column.columnId)));
        return missingColumns.length === 0
            ? section
            : {...section, columns: [...section.columns, ...toPanelColumns(missingColumns)]};
    });
    const sections = hasUtmSection
        ? updatedSections
        : [
              ...updatedSections,
              {
                  id: AMARON_EVENT_ATTRIBUTION_VIEW_SECTION_IDS[eventTemplate.id]!,
                  displayName: "UTM Parameters",
                  iconId: AnankeLinkExternalIcon,
                  iconColor: "var(--ds-icon-accent-purple)",
                  iconBackgroundColor: "var(--ds-background-accent-purple-subtlest)",
                  initialIsOpen: true,
                  columns: toPanelColumns(attributionColumns),
              },
          ];

    return {
        ...eventTemplate,
        configuration: {
            ...eventTemplate.configuration,
            eventPage: {
                ...eventTemplate.configuration.eventPage,
                viewPanel: {
                    ...viewPanel,
                    sections,
                },
            },
        },
    };
}

const AMARON_SERVICE_BOOKED_ON_TABLE_COLUMN: OttopilotEventTableViewColumn = {
    id: AMARON__EVENT__SERVICE__BOOKED_ON__COLUMN_ID,
    field: AMARON__EVENT__SERVICE__BOOKED_ON__COLUMN_ID,
    isAdditionalColumn: true,
    columnType: "OttopilotEvent",
    type: UdoColumnType.timestamp,
    displayName: "Booked On",
    sortingCapability: true,
    filteringCapability_contains: true,
};

function ensureAmaronServiceBookedOnTableColumn(tableViewColumns: Array<OttopilotEventTableViewColumn>): Array<OttopilotEventTableViewColumn> {
    const hasBookedOn = tableViewColumns.some((column) => column.id === AMARON__EVENT__SERVICE__BOOKED_ON__COLUMN_ID);
    if (hasBookedOn === true) {
        return tableViewColumns;
    }

    const createdAtIndex = tableViewColumns.findIndex((column) => column.field === "createdAt");
    const insertIndex = createdAtIndex >= 0 ? createdAtIndex + 1 : tableViewColumns.length;
    return [...tableViewColumns.slice(0, insertIndex), AMARON_SERVICE_BOOKED_ON_TABLE_COLUMN, ...tableViewColumns.slice(insertIndex)];
}

function ensureAmaronServiceBookedOnInActivityDetailsViewPanel(eventTemplate: OttopilotEventTemplate): OttopilotEventTemplate {
    const viewPanel = eventTemplate.configuration.eventPage?.viewPanel;
    if (viewPanel === undefined) {
        return eventTemplate;
    }

    const serviceTemplateId = AmaronEventTypes.Service as Uuid;
    const bookedOnViewColumn = {
        id: uniqueUuid`amaron-service-view-activity-booked-on`,
        label: "Booked On",
        columnId: AMARON__EVENT__SERVICE__BOOKED_ON__COLUMN_ID,
        columnType: "OttopilotEvent" as const,
        eventTemplateId: serviceTemplateId,
        isAdditionalColumn: true,
    };

    const sections = viewPanel.sections.map((section) => {
        if (section.kind === OttopilotEventViewPanelSectionKind.eventOverview) {
            return section;
        }
        if (normalizeLabel(section.displayName) !== "activity details") {
            return section;
        }

        const hasBookedOn = section.columns.some((column) => column.columnId === AMARON__EVENT__SERVICE__BOOKED_ON__COLUMN_ID);
        if (hasBookedOn === true) {
            return section;
        }

        const createdAtIndex = section.columns.findIndex((column) => column.columnId === "createdAt");
        const insertIndex = createdAtIndex >= 0 ? createdAtIndex + 1 : section.columns.length;
        return {
            ...section,
            columns: [...section.columns.slice(0, insertIndex), bookedOnViewColumn, ...section.columns.slice(insertIndex)],
        };
    });

    return {
        ...eventTemplate,
        configuration: {
            ...eventTemplate.configuration,
            eventPage: {
                ...eventTemplate.configuration.eventPage,
                viewPanel: {
                    ...viewPanel,
                    sections: sections,
                },
            },
        },
    };
}

/** Patches Service template table + view panel when workspace config is missing Booked On. */
export function buildAmaronServiceEventTemplate(params: {eventTemplate: OttopilotEventTemplate}): OttopilotEventTemplate {
    const {eventTemplate} = params;
    if (eventTemplate.id !== (AmaronEventTypes.Service as Uuid)) {
        return eventTemplate;
    }

    const tableViewColumns = ensureAmaronServiceBookedOnTableColumn(eventTemplate.configuration.eventPage?.tableViewColumns ?? []);

    const withTableViewColumns: OttopilotEventTemplate = {
        ...eventTemplate,
        configuration: {
            ...eventTemplate.configuration,
            eventPage: {
                ...eventTemplate.configuration.eventPage,
                tableViewColumns: tableViewColumns,
            },
        },
    };

    return ensureAmaronServiceBookedOnInActivityDetailsViewPanel(withTableViewColumns);
}

export function buildAmaronCallViewPanelTemplate(getAdditionalColumns: CapabilityUdoColumnResolver, params: {
    eventTemplate: OttopilotEventTemplate;
    journeysModuleConfiguration: JourneysModuleConfiguration;
}): OttopilotEventTemplate {
    const {eventTemplate, journeysModuleConfiguration} = params;
    if (eventTemplate.id !== (AmaronEventTypes.Calls as Uuid)) {
        return eventTemplate;
    }

    const journeyColumnsByName = new Map(getAdditionalColumns(journeysModuleConfiguration).map((column) => [normalizeLabel(column.displayName), column.id]));
    const eventColumnsByName = new Map(getAdditionalColumns(eventTemplate).map((column) => [normalizeLabel(column.displayName), column.id]));

    const getJourneyColumnId = (displayNames: string[]): Uuid | null => {
        for (const displayName of displayNames) {
            const id = journeyColumnsByName.get(normalizeLabel(displayName));
            if (id !== undefined) return id;
        }
        return null;
    };
    const getEventColumnId = (displayNames: string[]): Uuid | null => {
        for (const displayName of displayNames) {
            const id = eventColumnsByName.get(normalizeLabel(displayName));
            if (id !== undefined) return id;
        }
        return null;
    };

    type ViewPanelColumn =
        | {id: Uuid; label: string; columnId: Uuid | string; isAdditionalColumn: true; columnType: "OttopilotEventContainer"}
        | {id: Uuid; label: string; columnId: Uuid | string; isAdditionalColumn: true; columnType: "OttopilotEvent"; eventTemplateId: Uuid}
        | {
              id: Uuid;
              label: string;
              columnId: "createdAt" | "createdBy" | "updatedAt" | "updatedBy";
              isAdditionalColumn: false;
              columnType: "OttopilotEvent";
              eventTemplateId: Uuid;
          };

    const toSectionColumns = (
        rows: Array<
            | {label: string; columnType: "OttopilotEventContainer"; isAdditionalColumn: true; displayNames: string[]; columnId?: Uuid}
            | {label: string; columnType: "OttopilotEvent"; isAdditionalColumn: true; displayNames: string[]; columnId?: Uuid}
            | {label: string; columnType: "Auto"; isAdditionalColumn: true; displayNames: string[]; columnId?: Uuid}
            | {label: string; columnType: "OttopilotEvent"; isAdditionalColumn: false; columnId: "createdAt" | "createdBy" | "updatedAt" | "updatedBy"}
        >,
    ): Array<ViewPanelColumn> => {
        const rowStableId = (rowLabel: string, dataColumnId: Uuid | string): Uuid => `${String(dataColumnId)}::${normalizeLabel(rowLabel)}` as Uuid;
        return rows
            .map((row) => {
                if (row.isAdditionalColumn === false) {
                    return {
                        id: `amaron-call-${row.columnType}-${row.columnId}-${row.label}` as Uuid,
                        label: row.label,
                        columnId: row.columnId,
                        isAdditionalColumn: false as const,
                        columnType: "OttopilotEvent" as const,
                        eventTemplateId: eventTemplate.id,
                    };
                }
                if (row.columnType === "OttopilotEventContainer") {
                    const columnId = row.columnId ?? getJourneyColumnId(row.displayNames);
                    if (columnId === null) return null;
                    return {
                        id: rowStableId(row.label, columnId),
                        label: row.label,
                        columnId,
                        isAdditionalColumn: true as const,
                        columnType: "OttopilotEventContainer" as const,
                    };
                }
                if (row.columnType === "Auto") {
                    const journeyColumnId = getJourneyColumnId(row.displayNames);
                    if (journeyColumnId !== null) {
                        return {
                            id: rowStableId(row.label, journeyColumnId),
                            label: row.label,
                            columnId: journeyColumnId,
                            isAdditionalColumn: true as const,
                            columnType: "OttopilotEventContainer" as const,
                        };
                    }
                    const eventColumnId = getEventColumnId(row.displayNames) ?? row.columnId ?? null;
                    if (eventColumnId !== null) {
                        return {
                            id: rowStableId(row.label, eventColumnId),
                            label: row.label,
                            columnId: eventColumnId,
                            isAdditionalColumn: true as const,
                            columnType: "OttopilotEvent" as const,
                            eventTemplateId: eventTemplate.id,
                        };
                    }
                    return null;
                }
                const eventColumnId = getEventColumnId(row.displayNames) ?? row.columnId ?? null;
                if (eventColumnId === null) return null;
                return {
                    id: rowStableId(row.label, eventColumnId),
                    label: row.label,
                    columnId: eventColumnId,
                    isAdditionalColumn: true as const,
                    columnType: "OttopilotEvent" as const,
                    eventTemplateId: eventTemplate.id,
                };
            })
            .filter((item): item is NonNullable<typeof item> => item !== null);
    };

    return {
        ...eventTemplate,
        configuration: {
            ...eventTemplate.configuration,
            eventPage: {
                ...eventTemplate.configuration.eventPage,
                viewPanel: {
                    sections: [
                        {
                            id: uniqueUuid`amaron-call-view-customer-details`,
                            displayName: "Customer Details",
                            iconId: AnankePersonIcon,
                            iconColor: "var(--ds-icon-accent-blue)",
                            iconBackgroundColor: "var(--ds-background-accent-blue-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Customer Name",
                                    columnType: "OttopilotEventContainer",
                                    columnId: AMARON__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Customer Name", "customer name"],
                                },
                                {
                                    label: "Contact Number",
                                    columnType: "OttopilotEventContainer",
                                    columnId: AMARON__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Contact Number", "Contact number", "Phone Number"],
                                },
                                {
                                    label: "Customer State",
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__CUSTOMER_STATE__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Customer State", "State"],
                                },
                                {
                                    label: "Customer City",
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__CUSTOMER_CITY__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Customer City", "City"],
                                },
                            ]),
                        },
                        {
                            id: uniqueUuid`amaron-call-view-call-details`,
                            displayName: "Call Details",
                            iconId: AnankeTagIcon,
                            iconColor: "var(--ds-icon-accent-teal)",
                            iconBackgroundColor: "var(--ds-background-accent-teal-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Call Status",
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__CALL_STATUS__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Call Status", "Status"],
                                },
                                {
                                    label: "L1 Disposition",
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__L1_DISPOSITION__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["L1 Disposition", "L1 disposition"],
                                },
                                {
                                    label: "L2 Disposition",
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__L2_DISPOSITION__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["L2 Disposition", "L2 disposition"],
                                },
                                {
                                    label: "L3 Disposition",
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__L3_DISPOSITION__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["L3 Disposition", "L3 disposition"],
                                },
                                {
                                    label: "L4 Disposition",
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__L4_DISPOSITION__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["L4 Disposition", "L4 disposition"],
                                },
                                {
                                    label: "Remarks",
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__REMARKS__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Remarks", "Disposition Remarks"],
                                },
                                {
                                    label: "Last Call Date",
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__LAST_CALL_DATE__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Last Call Date", "Last call date"],
                                },
                                {
                                    label: "Call Count",
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__CALL_COUNT__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Call Count", "call count"],
                                },
                                {
                                    label: "Assigned Agent",
                                    columnType: "OttopilotEventContainer",
                                    columnId: AMARON__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Assigned Agent", "assigned agent"],
                                },
                                {
                                    label: "Action Platform",
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__ACTION_PLATFORM__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Action Platform", "action platform", "Platform"],
                                },
                            ]),
                        },
                        {
                            id: uniqueUuid`amaron-call-view-activity-details`,
                            displayName: "Activity Details",
                            iconId: AnankeClockIcon,
                            iconColor: "var(--ds-icon-accent-yellow)",
                            iconBackgroundColor: "var(--ds-background-accent-yellow-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Scheduled By",
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__SCHEDULED_BY__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Scheduled By", "Scheduled by"],
                                },
                                {
                                    label: "Scheduled On",
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__SCHEDULED_ON__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Scheduled On", "Scheduled on"],
                                },
                                {
                                    label: "Logged By",
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__LOGGED_BY__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Logged By", "Logged by"],
                                },
                                {
                                    label: "Logged On",
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__LOGGED_ON__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Logged On", "Logged on"],
                                },
                                {
                                    label: "Cancelled By",
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__CANCELLED_BY__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Cancelled By", "Cancelled by"],
                                },
                                {
                                    label: "Cancelled On",
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__CANCELLED_ON__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Cancelled On", "Cancelled on"],
                                },
                                {
                                    label: "Cancellation Reason",
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__CANCELLATION_REASON__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Cancellation Reason", "Cancellation Remarks", "Cancelation Reason"],
                                },
                                {label: "Modified By", columnType: "OttopilotEvent", isAdditionalColumn: false, columnId: "updatedBy"},
                                {label: "Modified On", columnType: "OttopilotEvent", isAdditionalColumn: false, columnId: "updatedAt"},
                            ]),
                        },
                        {
                            id: uniqueUuid`amaron-call-view-utm-parameters`,
                            displayName: "UTM Parameters",
                            iconId: AnankeLinkExternalIcon,
                            iconColor: "var(--ds-icon-accent-purple)",
                            iconBackgroundColor: "var(--ds-background-accent-purple-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Utm Platform",
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__UTM_PLATFORM__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_platform", "utm_platform", "UTM Platform", "Utm Platform"],
                                },
                                {
                                    label: "Utm Source",
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__UTM_SOURCE__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_source", "utm_source", "UTM Source", "Utm Source"],
                                },
                                {
                                    label: "Utm Medium",
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__UTM_MEDIUM__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_medium", "utm_medium", "UTM Medium", "Utm Medium"],
                                },
                                {
                                    label: "Utm Campaign",
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__UTM_CAMPAIGN__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_campaign", "utm_campaign", "UTM Campaign", "Utm Campaign"],
                                },
                                {
                                    label: "Utm Term",
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__UTM_TERM__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_term", "utm_term", "UTM Term", "Utm Term"],
                                },
                                {
                                    label: "Utm Content",
                                    columnType: "OttopilotEvent",
                                    columnId: AMARON__EVENT__CALLS__UTM_CONTENT__COLUMN_ID,
                                    isAdditionalColumn: true,
                                    displayNames: ["Utm_content", "utm_content", "UTM Content", "Utm Content"],
                                },
                                {label: "Google Ads Source", columnType: "OttopilotEvent", columnId: AMARON__EVENT__CALLS__GAD_SOURCE__COLUMN_ID, isAdditionalColumn: true, displayNames: ["gadSource", "gad_source", "Google Ads Source"]},
                                {label: "Google Ads Campaign ID", columnType: "OttopilotEvent", columnId: AMARON__EVENT__CALLS__GAD_CAMPAIGN_ID__COLUMN_ID, isAdditionalColumn: true, displayNames: ["gadCampaignId", "gad_campaignid", "Google Ads Campaign ID"]},
                                {label: "GBRAID", columnType: "OttopilotEvent", columnId: AMARON__EVENT__CALLS__GBRAID__COLUMN_ID, isAdditionalColumn: true, displayNames: ["gbraid", "GBRAID"]},
                                {label: "GCLID", columnType: "OttopilotEvent", columnId: AMARON__EVENT__CALLS__GCLID__COLUMN_ID, isAdditionalColumn: true, displayNames: ["gclid", "GCLID"]},
                                {label: "Facebook Ads Source", columnType: "OttopilotEvent", columnId: AMARON__EVENT__CALLS__FAD_SOURCE__COLUMN_ID, isAdditionalColumn: true, displayNames: ["fadSource", "fad_source", "Facebook Ads Source"]},
                                {label: "Facebook Ads Campaign ID", columnType: "OttopilotEvent", columnId: AMARON__EVENT__CALLS__FAD_CAMPAIGN_ID__COLUMN_ID, isAdditionalColumn: true, displayNames: ["fadCampaignId", "fad_campaignid", "Facebook Ads Campaign ID"]},
                                {label: "FBRAID", columnType: "OttopilotEvent", columnId: AMARON__EVENT__CALLS__FBRAID__COLUMN_ID, isAdditionalColumn: true, displayNames: ["fbraid", "FBRAID"]},
                                {label: "FBCLID", columnType: "OttopilotEvent", columnId: AMARON__EVENT__CALLS__FBCLID__COLUMN_ID, isAdditionalColumn: true, displayNames: ["fbclid", "FBCLID"]},
                            ]),
                        },
                    ],
                },
            },
        },
    };
}
